import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Pagination,
} from "@mui/material";
import LazyImage from "../../components/common/LazyImage";
import {
  Search as SearchIcon,
  PlayArrow,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useAuth } from "../../contexts/AuthContext";
import { videosAPI } from "../../services/api";
import { useTranslation } from "react-i18next";
import CustomContainer from "../../components/common/CustomContainer";

function VideoCard({ video }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const apiBaseUrl =
    import.meta.env.VITE_API_URL || "https://ebibliospace.com/api";

  const thumbnailUrl = video.thumbnail_url
    ? video.thumbnail_url.startsWith("http")
      ? video.thumbnail_url
      : video.thumbnail_url.startsWith("/")
      ? `${apiBaseUrl}${video.thumbnail_url}`
      : `${apiBaseUrl}/${video.thumbnail_url}`
    : "/placeholder-thumbnail.jpg";

  // console.log("Video thumbnail URL:", thumbnailUrl);

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          position: "relative",
          paddingTop: "56.25%",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <LazyImage src={thumbnailUrl} alt={video.title} />
        </Box>
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="div" noWrap>
          {video.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {video.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("videos.details.duration")}: {Math.floor(video.duration / 60)}:
          {String(video.duration % 60).padStart(2, "0")}
        </Typography>
      </CardContent>
      <CardActions>
        <Button
          size="small"
          startIcon={<PlayArrow />}
          onClick={() => navigate(`/videos/${video.id}`)}
        >
          {t("videos.list.watchVideo")}
        </Button>
      </CardActions>
    </Card>
  );
}

function VideoList() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    sort: "title",
    duration: "all",
  });

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
  });

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await videosAPI.getAll();
      let filteredVideos = [...response.data];

      // Apply search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredVideos = filteredVideos.filter(
          (video) =>
            video.title.toLowerCase().includes(searchTerm) ||
            video.description.toLowerCase().includes(searchTerm)
        );
      }

      // Apply duration filter
      if (filters.duration !== "all") {
        filteredVideos = filteredVideos.filter((video) => {
          const duration = video.duration;
          switch (filters.duration) {
            case "short":
              return duration < 300;
            case "medium":
              return duration >= 300 && duration <= 900;
            case "long":
              return duration > 900;
            default:
              return true;
          }
        });
      }

      // Apply sorting
      switch (filters.sort) {
        case "title":
          filteredVideos.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case "date":
          filteredVideos.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          break;
        case "duration":
          filteredVideos.sort((a, b) => b.duration - a.duration);
          break;
      }

      // Apply pagination
      const itemsPerPage = 9;
      const totalPages = Math.ceil(filteredVideos.length / itemsPerPage);
      const start = (pagination.page - 1) * itemsPerPage;
      const paginatedVideos = filteredVideos.slice(start, start + itemsPerPage);

      setVideos(paginatedVideos);
      setPagination((prev) => ({
        ...prev,
        totalPages,
      }));
    } catch (error) {
      console.error("Error fetching videos:", error);
      showSnackbar(t("videos.messages.loadError"), "error");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, showSnackbar, t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleFilterChange = (name, value) => {
    if (name === "search") {
      setSearchInput(value);
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CustomContainer>
      {/* Filters */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder={t("videos.list.searchPlaceholder")}
              value={searchInput}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>{t("videos.filters.duration")}</InputLabel>
              <Select
                value={filters.duration}
                label={t("videos.filters.duration")}
                onChange={(e) => handleFilterChange("duration", e.target.value)}
              >
                <MenuItem value="all">
                  {t("videos.filters.allDurations")}
                </MenuItem>
                <MenuItem value="short">
                  {t("videos.filters.shortVideos")}
                </MenuItem>
                <MenuItem value="medium">
                  {t("videos.filters.mediumVideos")}
                </MenuItem>
                <MenuItem value="long">
                  {t("videos.filters.longVideos")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              endIcon={<FilterIcon />}
              onClick={handleMenuOpen}
            >
              {t("videos.filters.sort")}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Sort Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleFilterChange("sort", "title");
            handleMenuClose();
          }}
        >
          {t("videos.sort.byTitle")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleFilterChange("sort", "date");
            handleMenuClose();
          }}
        >
          {t("videos.sort.byDate")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleFilterChange("sort", "duration");
            handleMenuClose();
          }}
        >
          {t("videos.sort.byDuration")}
        </MenuItem>
      </Menu>

      {/* Videos Grid */}
      {videos.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {videos.map((video) => (
              <Grid item key={video.id} xs={12} sm={6} md={4}>
                <VideoCard video={video} />
              </Grid>
            ))}
          </Grid>
          {pagination.totalPages > 1 && (
            <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, value) =>
                  setPagination((prev) => ({ ...prev, page: value }))
                }
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t("videos.list.noVideos")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("videos.list.adjustFilters")}
          </Typography>
        </Box>
      )}
    </CustomContainer>
  );
}

export default VideoList;
